import { message } from "antd";
import { concat, from, of } from "rxjs";
import { catchError, filter, switchMap, withLatestFrom } from "rxjs/operators";
import { startLoading, stopLoading } from "../loading";
import type { RootEpic } from "../types";
import { uploadFileActions } from "@/features/upload/stores/uploadFiles/uploadFileSlice";
import deleteFileService from "./deleteFileService";
import { deleteFileActions } from "./deleteFileSlice";
import { FolderService } from "@/services/FolderService";
import { DeletedFilesApiResponse } from "@/features/file-management/pages/FilePage/services/files.service";
import { fileActions } from "../file/fileSlice";
import { handleHttpError } from "@/api/HttpClient";
import i18next from "i18next";
import { navigationActions } from "../navigation";

export const deleteFileEpic$: RootEpic = (action$, state$) =>
  action$.pipe(
    filter(deleteFileActions.deleteFileRequest.match),
    withLatestFrom(state$),
    switchMap(([action, state]) => {
      const { documentIds, totalSize } = action.payload;
      const searchQuery = state.search?.query;
      const selectedOption = state.search?.selectedOption || "file";
      const companyId = parseInt(localStorage.getItem("companyId") || "1", 10);
      const projectId = localStorage.getItem("projectId") || "0";

      return concat(
        [startLoading({ key: "deleteFile" })],
        deleteFileService.markFileAsDeleted(documentIds, true).pipe(
          switchMap((response) => {
            message.success(
              response.message ||
                i18next.t("trashpage:file_deleted_successfully"),
            );

            const actions = [
              deleteFileActions.deleteFileSuccess(),
              uploadFileActions.setLastUploadAt(Date.now()),
              ...(totalSize && totalSize > 0
                ? [
                    // fileActions.updateUserStorageSize(-totalSize),
                    uploadFileActions.updateCompanyStorageSize(-totalSize),
                  ]
                : []),
            ];

            return from(actions);
          }),
          catchError((err) => {
            message.error(handleHttpError(err));
            console.error(
              err.message || i18next.t("trashpage:error_deleting_file"),
            );
            return of(
              deleteFileActions.deleteFileFailure(
                err.message || i18next.t("trashpage:error_deleting_file"),
              ),
            );
          }),
        ),
        // If search is active, trigger search reload as separate action
        ...(searchQuery && searchQuery.trim()
          ? [
              of(
                fileActions.searchFilesRequest({
                  companyId,
                  projectId,
                  ...(selectedOption === "file"
                    ? { fileNameKey: searchQuery }
                    : selectedOption === "content"
                      ? { contentKey: searchQuery }
                      : selectedOption === "total"
                        ? { fileNameKey: searchQuery, contentKey: searchQuery }
                        : {}),
                }),
              ),
            ]
          : []),
        [stopLoading({ key: "deleteFile" })],
      );
    }),
  );

export const deleteMultipleFoldersEpic$: RootEpic = (action$, state$) =>
  action$.pipe(
    filter(deleteFileActions.deleteMultipleFoldersRequest.match),
    withLatestFrom(state$),
    switchMap(([action, state]) => {
      const { folderIds, totalSize } = action.payload;
      const count = folderIds.length;
      const searchQuery = state.search?.query;
      const selectedOption = state.search?.selectedOption || "file";
      const companyId = parseInt(localStorage.getItem("companyId") || "1", 10);
      const projectId = localStorage.getItem("projectId") || "0";

      return concat(
        [startLoading({ key: "deleteMultipleFolders" })],
        FolderService.Patch.markFoldersAsDeleted(folderIds).pipe(
          switchMap((_response) => {
            message.success(
              i18next.t("trashpage:folders_deleted_successfully", { count }),
            );

            const actions = [
              deleteFileActions.deleteFileSuccess(),
              uploadFileActions.setLastUploadAt(Date.now()),
              ...(totalSize && totalSize > 0
                ? [uploadFileActions.updateCompanyStorageSize(-totalSize)]
                : []),
              ...folderIds.map((folderId) =>
                navigationActions.removeBreadcrumb(folderId)
              ), 
            ];

            return from(actions);
          }),
          catchError((err) => {
            message.error(handleHttpError(err));
            const errorMessage = i18next.t("trashpage:error_deleting_folders", {
              count,
              error: err.message || i18next.t("trashpage:error_deleting_file"),
            });
            console.error(errorMessage);
            return of(deleteFileActions.deleteFileFailure(errorMessage));
          }),
        ),
        // If search is active, trigger search reload as separate action
        ...(searchQuery && searchQuery.trim()
          ? [
              of(
                fileActions.searchFilesRequest({
                  companyId,
                  projectId,
                  ...(selectedOption === "file"
                    ? { fileNameKey: searchQuery }
                    : selectedOption === "content"
                      ? { contentKey: searchQuery }
                      : selectedOption === "total"
                        ? { fileNameKey: searchQuery, contentKey: searchQuery }
                        : {}),
                }),
              ),
            ]
          : []),
        [stopLoading({ key: "deleteMultipleFolders" })],
      );
    }),
  );

export const getDeletedFilesEpic$: RootEpic = (action$, _state$) =>
  action$.pipe(
    filter(deleteFileActions.getDeletedFilesRequest.match),
    switchMap((action) => {
      const { userId, page = 1, pageSize = 20 } = action.payload;

      return deleteFileService.getDeletedFiles(userId, page, pageSize).pipe(
        switchMap((response: DeletedFilesApiResponse) => {
          // console.log("Deleted files response:", response);
          return from([
            deleteFileActions.getDeletedFilesSuccess({
              files: response.results || [],
              total: response.queryCount || 0,
              page: response.page || page,
              pageSize: response.pageSize || pageSize,
              pageCount: response.pageCount || 0,
              firstRowIndex: response.firstRowIndex || 0,
              lastRowIndex: response.lastRowIndex || 0,
            }),
            // Also sync global file pagination so tables use consistent pagination state
            // pass raw response; reducer will compute derived fields
            fileActions.setPagination(response),
          ]);
        }),
        catchError((err) => {
          message.error(handleHttpError(err));
          console.error(
            err.message || i18next.t("trashpage:error_loading_deleted_files"),
          );
          return of(
            deleteFileActions.getDeletedFilesFailure(
              err.message || i18next.t("trashpage:error_loading_deleted_files"),
            ),
          );
        }),
      );
    }),
  );

export const restoreFileEpic$: RootEpic = (action$, _state$) =>
  action$.pipe(
    filter(deleteFileActions.restoreFileRequest.match),
    switchMap((action) => {
      const { documentIds } = action.payload;

      return concat(
        [startLoading({ key: "restoreFile" })],
        deleteFileService.restoreFile(documentIds).pipe(
          switchMap((response) => {
            message.success(
              response.message ||
                i18next.t("trashpage:file_restored_successfully"),
            );

            return from([
              deleteFileActions.deleteFileSuccess(),
              // Trigger reload data
              uploadFileActions.setLastUploadAt(Date.now()),
            ]);
          }),
          catchError((err) => {
            message.error(handleHttpError(err));
            console.error(
              err.message || i18next.t("trashpage:error_restoring_file"),
            );
            return of(
              deleteFileActions.deleteFileFailure(
                err.message || i18next.t("trashpage:error_restoring_file"),
              ),
            );
          }),
        ),
        [stopLoading({ key: "restoreFile" })],
      );
    }),
  );

export const restoreFoldersEpic$: RootEpic = (action$, _state$) =>
  action$.pipe(
    filter(deleteFileActions.restoreFoldersRequest.match),
    switchMap((action) => {
      const { folderIds } = action.payload;
      const count = folderIds.length;

      return concat(
        [startLoading({ key: "restoreFolders" })],
        FolderService.Patch.restoreFolders(folderIds).pipe(
          switchMap((_response) => {
            message.success(
              i18next.t("trashpage:folders_restored_successfully", {
                count,
                plural: count > 1 ? "s" : "",
              }),
            );

            return from([
              deleteFileActions.deleteFileSuccess(),
              // Trigger reload data
              uploadFileActions.setLastUploadAt(Date.now()),
            ]);
          }),
          catchError((err) => {
            console.error("Error while restoring folders:", err);
            message.error(handleHttpError(err));

            const errorMessage =
              count > 1
                ? i18next.t("trashpage:failed_restore_folders", { count })
                : i18next.t("trashpage:failed_restore_folder");

            console.error(errorMessage);

            return of(deleteFileActions.deleteFileFailure(errorMessage));
          }),
        ),
        [stopLoading({ key: "restoreFolders" })],
      );
    }),
  );

export const forceDeleteFilesEpic$: RootEpic = (action$, _state$) =>
  action$.pipe(
    filter(deleteFileActions.forceDeleteFilesRequest.match),
    switchMap((action) => {
      const { fileIds, totalSize } = action.payload;
      const count = fileIds.length;

      // Lấy danh sách documentIds từ files
      // Tính tổng dung lượng các file bị xóa
      const documentCount = fileIds.length;
      return concat(
        [startLoading({ key: "forceDeleteFiles" })],
        deleteFileService.forceDeleteFiles(fileIds).pipe(
          switchMap((response) => {
            message.success(i18next.t("trashpage:file_permanently_deleted"));

            // Cập nhật storage nếu có totalSize
            const actions = [
              deleteFileActions.deleteFileSuccess(),
              // Trigger reload data
              uploadFileActions.setLastUploadAt(Date.now()),
              ...(totalSize && totalSize > 0
                ? [
                    fileActions.updateUserStorageSize(-totalSize),
                    uploadFileActions.updateCompanyStorageSize(-totalSize),
                  ]
                : []),
            ];

            return from(actions);
          }),
          catchError((err) => {
            const errorMessage = i18next.t(
              "trashpage:error_permanently_deleting_files",
              {
                count,
                error: err.message || "Unknown error",
              },
            );
            console.error(errorMessage);
            message.error(handleHttpError(err));
            return of(deleteFileActions.deleteFileFailure(errorMessage));
          }),
        ),
        [stopLoading({ key: "forceDeleteFiles" })],
      );
    }),
  );

export const forceDeleteFoldersEpic$: RootEpic = (action$, state$) =>
  action$.pipe(
    filter(deleteFileActions.forceDeleteFoldersRequest.match),
    withLatestFrom(state$),
    switchMap(([action, state]) => {
      const { folderIds } = action.payload;
      const count = folderIds.length;
      const trashSource = state.trashPage?.trashSource;

      return concat(
        [startLoading({ key: "forceDeleteFolders" })],
        FolderService.Delete.forceDeleteFolders(folderIds).pipe(
          switchMap((response: any[]) => {
            // console.log("Force delete folders response:", response);

            const totalBytesDeleted = response.reduce(
              (sum, item) => sum + (item.totalBytesDeleted || 0),
              0,
            );

            // Always use frontend translation instead of backend message
            const successMessage = i18next.t(
              "trashpage:folders_permanently_deleted",
              { count },
            );

            message.success(successMessage);

            const actions = [
              deleteFileActions.deleteFileSuccess(),
              uploadFileActions.setLastUploadAt(Date.now()),
              ...(totalBytesDeleted > 0
                ? trashSource?.type === "my-drive"
                  ? [
                      fileActions.updateUserStorageSize(-totalBytesDeleted),
                      uploadFileActions.updateCompanyStorageSize(
                        -totalBytesDeleted,
                      ),
                    ]
                  : [
                      uploadFileActions.updateCompanyStorageSize(
                        -totalBytesDeleted,
                      ),
                    ]
                : []),
            ];

            return from(actions);
          }),
          catchError((_err) => {
            message.error(handleHttpError(_err));
            const errorMessage = i18next.t(
              "trashpage:error_permanently_deleting_folders",
              { count },
            );
            console.error(errorMessage);
            return of(deleteFileActions.deleteFileFailure(errorMessage));
          }),
        ),
        [stopLoading({ key: "forceDeleteFolders" })],
      );
    }),
  );

export const deleteFileEpics = [
  deleteFileEpic$,
  deleteMultipleFoldersEpic$,
  getDeletedFilesEpic$,
  restoreFileEpic$,
  restoreFoldersEpic$,
  forceDeleteFilesEpic$,
  forceDeleteFoldersEpic$,
];
